import React, { useEffect, useRef, useState } from 'react';
import styles from './TicketTypesSummary.module.scss';

import {
  NoDataTableRow,
  Pagination,
  SearchField,
  SummaryHeaderWrapper,
  SummaryPageHeader,
  SummaryTableControls,
  SummaryWrapper,
  TableWrapper
} from 'components/SummaryComponents';
import TableHeadComponent from 'components/SummaryComponents/TableHeadComponent/TableHeadComponent';
import { StyledTableCell, StyledTableRow } from 'components/StyledComponents';
import { StyledButton } from 'components/Buttons';
import StyledCheckbox from 'components/StyledComponents/StyledCheckbox';

import { IconButton, Table, TableBody } from '@material-ui/core';
import CreateIcon from '@material-ui/icons/Create';

import { useUserConfig } from 'hooks/useUserConfig';
import { useMobileViewport } from 'hooks/useMobileViewport';
import { useCommonActions, useCommonSelector } from 'hooks/Common';
import { useTicketTypesActions, useTicketTypesSelector } from 'hooks/TicketTypes';
import { useHistory } from 'react-router-dom';
import useScrollManager from 'hooks/useScrollManager';

import { joinWithComma, pluck } from 'helpers/AppHelpers';
import {
  getChangedByLimitFilter,
  getChangedByPageFilter,
  getChangedBySearchFilter,
  getChangedBySortFilter
} from 'helpers/SummaryPagesHelpers';

import { TICKET_TYPES_TABLE_PARAMETER } from 'constants/configTableConstants';
import { TICKET_TYPE_EDIT_PATH } from 'constants/routeConstants';

import { tableConfig } from './tableConfig';

const defaultFilter = {
  filters: {
    searchQuery: ''
  },
  pagination: {
    page: 1,
    totalPages: 1,
    limit: 10
  },
  sortRules: [],
  responseReceived: false
};

export default function TicketTypesSummary() {
  const isMobile = useMobileViewport();
  const tableRef = useRef(null);
  const history = useHistory();

  const [tableState, setTableState] = useState([]);

  const { filter } = useTicketTypesSelector();
  const { getTicketTypesAction, setFilterAction, clearStateAction } = useTicketTypesActions();

  const { isConfigReceived, getTableLimit, isAllLimitEnabled } = useUserConfig();

  const { setLoadMoreFlagAction } = useCommonActions();
  const { loadMoreFlag } = useCommonSelector();

  const { filters, pagination, responseReceived, sortRules } = filter;

  const { saveScrollPosition, removeScrollPosition } = useScrollManager({
    tableKey: TICKET_TYPES_TABLE_PARAMETER,
    isDataLoaded: responseReceived
  });

  const generateInitialQuery = () => ({
    filters: { ...defaultFilter.filters },
    pagination: {
      page:
        getTableLimit(TICKET_TYPES_TABLE_PARAMETER) === 100 &&
        isAllLimitEnabled(TICKET_TYPES_TABLE_PARAMETER)
          ? defaultFilter.pagination.page
          : pagination?.page || defaultFilter.pagination.page,
      limit: getTableLimit(TICKET_TYPES_TABLE_PARAMETER)
    },
    sortRules: sortRules || defaultFilter.sortRules,
    responseReceived: false
  });

  const getTicketTypes = (query, isAutoload) => {
    setFilterAction(query);

    getTicketTypesAction(query).then((tableData) => {
      setTicketTypes(tableData, query, isAutoload);
      !loadMoreFlag && setLoadMoreFlagAction(false);
    });
  };

  const setTicketTypes = (data, query, isAutoload) => {
    if (!isAutoload) {
      setTableState(data?.items || []);
    } else {
      setTableState(tableState.concat(data?.items || []));
    }

    const updatedFilter = {
      ...query,
      pagination: { limit: data.limit, page: data.pageNumber, totalPages: data.totalPages },
      responseReceived: true
    };
    setFilterAction(updatedFilter);
  };

  useEffect(() => {
    if (!isConfigReceived) return;

    getTicketTypes(generateInitialQuery());
  }, [isConfigReceived]);

  useEffect(() => {
    return () => {
      clearStateAction();
    };
  }, []);

  const findMatches = (value) => getTicketTypes(getChangedBySearchFilter(filter, value));
  const sortTable = (rowName) => getTicketTypes(getChangedBySortFilter(filter, rowName));
  const changePage = (page, param) =>
    getTicketTypes(getChangedByPageFilter(filter, page), param === 'AUTOLOAD');
  const changeLimit = () =>
    getTicketTypes(getChangedByLimitFilter(filter, getTableLimit(TICKET_TYPES_TABLE_PARAMETER)));

  const createTicketType = () => {
    removeScrollPosition();
    history.push(TICKET_TYPE_EDIT_PATH);
  };
  const editTicketType = (id) => {
    saveScrollPosition();
    history.push(`${TICKET_TYPE_EDIT_PATH}/${id}`);
  };

  return (
    <SummaryWrapper>
      <SummaryHeaderWrapper>
        <SummaryPageHeader title="Asset Ticket Types">
          {!isMobile && (
            <StyledButton
              label="Add new type"
              onClick={createTicketType}
              classes={styles.main_button}
            />
          )}
        </SummaryPageHeader>
        <SummaryTableControls>
          <SearchField filterValue={filters?.searchQuery} onSearch={findMatches} />
        </SummaryTableControls>
      </SummaryHeaderWrapper>

      {isMobile && (
        <StyledButton
          label="Add new type"
          onClick={createTicketType}
          classes={styles.main_button}
        />
      )}

      <TableWrapper ref={tableRef}>
        <Table stickyHeader={!isMobile}>
          <TableHeadComponent config={tableConfig} sortRules={sortRules} onSortApply={sortTable} />
          <TableBody>
            {tableState?.length ? (
              tableState.map((row) => (
                <StyledTableRow key={row.id} className={styles.ticket_type_table_row}>
                  <StyledTableCell className={styles.nameCell}>{row?.name || ''}</StyledTableCell>
                  <StyledTableCell className={styles.severityCell}>
                    {joinWithComma(pluck(row.severities, 'displayName'))}
                  </StyledTableCell>
                  <StyledTableCell className={styles.reopeningCell}>
                    {row?.reopening ? <StyledCheckbox disabled checked={row?.reopening} /> : null}
                  </StyledTableCell>
                  <StyledTableCell className={styles.descriptionCell}>
                    {row?.description || ''}
                  </StyledTableCell>
                  <StyledTableCell className={styles.activeCell}>
                    {row?.active ? 'Active' : 'Not Active'}
                  </StyledTableCell>
                  <StyledTableCell className={styles.prefixesCell}>
                    {row?.isSelectedAllAssetPrefixes
                      ? 'All Selected'
                      : joinWithComma(pluck(row.assetPrefixes, 'prefix'))}
                  </StyledTableCell>
                  <StyledTableCell className={styles.controlsCell}>
                    <div className={styles.controls}>
                      <IconButton
                        onClick={() => editTicketType(row.id)}
                        className={styles.icon_button}>
                        <CreateIcon />
                      </IconButton>
                    </div>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : responseReceived ? (
              <NoDataTableRow isTableFiltered={!!filters?.searchQuery} />
            ) : null}
          </TableBody>
        </Table>
      </TableWrapper>

      <Pagination
        withOptionAll
        rowsNumber={tableState?.length}
        currentPage={pagination?.page}
        totalPages={pagination?.totalPages}
        onChangePage={changePage}
        onChangeLimit={changeLimit}
        selectedLimit={pagination?.limit}
        tableName={TICKET_TYPES_TABLE_PARAMETER}
        tableRef={tableRef}
      />
    </SummaryWrapper>
  );
}
